/**
 * The browser web-funnel harness gate, shared by the probe and the PostHog manager.
 *
 * It has no imports so the manager can read the same pre-launch fact the probe
 * installs behind without loading the probe's tracker graph, which itself reaches
 * the manager. The three conditions and why each is required are documented on the
 * probe (web-funnel-probe.ts, "THE GATE").
 *
 * Per rule: think-in-systems (ai.knowledge/global) — one gate, two consumers.
 */

/**
 * The global the harness sets before any page script runs. Exported so the
 * Playwright side and this gate cannot drift to two different spellings.
 */
export const WEB_FUNNEL_HARNESS_GLOBAL = '__IRIS_E2E_WEB_FUNNEL_HARNESS__';

/**
 * The globals the gate reads. Taken as a parameter (defaulting to the real
 * `globalThis`) purely so the three conditions can be tested independently:
 * jsdom refuses to redefine `window.location.hostname`, so the loopback condition
 * is otherwise unassertable. The install below passes nothing, so the shipped
 * path reads the real globals.
 */
export interface WebFunnelGateScope extends Record<string, unknown> {
  window?: { electron?: unknown; location?: { hostname?: string } };
}

export const isBrowserE2eEnabled = (scope: WebFunnelGateScope = globalThis): boolean => {
  try {
    // Condition 1 — never in an Electron renderer.
    if (scope.window?.electron !== undefined) {
      return false;
    }

    // Condition 2 — loopback only, so no deployed web host can reach this.
    const hostname = scope.window?.location?.hostname;
    if (hostname !== '127.0.0.1' && hostname !== 'localhost') {
      return false;
    }

    // Condition 3 — the harness said so, strictly.
    return scope[WEB_FUNNEL_HARNESS_GLOBAL] === true;
  } catch {
    return false;
  }
};
